// Schedule a Demo · public marketing page.
//
// Product Owner-approved direction:
//   • Simple intake form (no calendar, no qualification gate).
//   • Approved hero background artwork lives at
//     /brand/schedule-a-demo/hero_levi_worktable_v1.png. Composition
//     shows Levi on the right; the live form + copy overlay the
//     intentional empty space on the left half of the image.
//   • JobClose AI public visual system — no red, no new design
//     language, no calendar UI, no payment UI.
//   • EN/ES driven through the same COPY dictionary used by the
//     rest of the landing marketing surfaces (landingCopy.js).
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/landing/landingPrimitives";
import { COPY } from "@/components/landing/landingCopy";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ClipboardCheck,
  MessageCircleQuestion,
  ShieldCheck,
  Users,
  Sparkles,
  CalendarCheck2,
  ArrowRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRADES = [
  "Roofing",
  "Flooring",
  "Stucco",
  "Painting",
  "Drywall",
  "General Contracting & Remodeling",
  "Restoration",
  "Concrete & Masonry",
  "Siding",
  "Gutters",
  "Handyman & General Repairs",
  "HVAC",
  "Fencing",
  "Decks & Patios",
  "Insulation",
  "Other",
];

// Rendered as-is in both languages. Backend stores the full label
// the visitor selects.
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

const inputBase =
  "w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] " +
  "bg-white text-[var(--jc-text)] placeholder:text-[var(--jc-text-soft)] " +
  "focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] focus:border-[var(--jc-blue)] transition";

const fieldLabel = "block text-xs font-semibold tracking-wide uppercase text-[var(--jc-text-soft)] mb-1.5";

export default function ScheduleDemo() {
  const { lang, setLang } = useLang();
  const c = COPY[lang] || COPY.en;
  const d = c.demo;

  return (
    <div className="min-h-screen bg-white" data-testid="schedule-demo-page">
      <Header c={c} lang={lang} setLang={setLang} activePage="demo" />
      <DemoHero d={d} lang={lang} />
      <DemoValueTiles d={d} />
      <DemoFAQ d={d} />
      <DemoFinalCTA d={d} />
    </div>
  );
}

// ── Hero: approved artwork as the full-width background, live copy
//    and form overlaid on the intentional empty space on the left.
//    Levi + mug + blueprint + tape occupy the right half untouched. ──
function DemoHero({ d, lang }) {
  return (
    <section
      className="relative border-b border-[var(--jc-border)]"
      data-testid="demo-hero"
    >
      {/* Desktop background — approved artwork spans the full hero.
          `object-right` keeps Levi + the mug pinned to the right so
          they never crop as the viewport widens. */}
      <div aria-hidden="true" className="hidden lg:block absolute inset-0 pointer-events-none">
        <img
          src="/brand/schedule-a-demo/hero_levi_worktable_v1.png"
          alt=""
          className="w-full h-full object-cover object-right select-none"
          draggable={false}
        />
        {/* Soft warm wash on the left so the copy reads without
            washing out Levi. Fades to fully transparent by ~55%
            of the width — Levi is untouched. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.88) 32%, rgba(255,255,255,0.55) 46%, rgba(255,255,255,0) 62%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-14 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
        <div className="lg:col-span-6 xl:col-span-5" data-testid="demo-hero-copy">
          <div
            className="text-[12px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)] mb-4"
            data-testid="demo-hero-eyebrow"
          >
            {d.eyebrow}
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-[54px] leading-[1.02] tracking-tight text-[var(--jc-text)]"
            data-testid="demo-hero-h1"
          >
            {d.h1}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[var(--jc-text-soft)] leading-relaxed max-w-xl">
            {d.subhead}
          </p>
          <ul className="mt-7 space-y-3 max-w-md">
            {d.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`demo-hero-bullet-${i}`}>
                <span className="mt-1 shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)]">
                  <Sparkles size={13} strokeWidth={2.6} />
                </span>
                <span className="text-[var(--jc-text)]">
                  <strong className="font-semibold">{b.title}</strong>{" "}
                  <span className="text-[var(--jc-text-soft)]">— {b.body}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <DemoForm d={d} lang={lang} />
          </div>

          {/* Mobile-only preview of the approved artwork so the composition still reads on phones. */}
          <div className="lg:hidden mt-8 rounded-2xl overflow-hidden ring-1 ring-black/5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)]">
            <img
              src="/brand/schedule-a-demo/hero_levi_worktable_v1.png"
              alt={d.hero_img_alt}
              className="w-full h-auto select-none"
              draggable={false}
              data-testid="demo-hero-img-mobile"
            />
          </div>
        </div>

        {/* Right column intentionally empty on desktop — Levi + mug live
            in the background artwork behind this space. */}
        <div className="hidden lg:block lg:col-span-6 xl:col-span-7 min-h-[540px]" aria-hidden="true" />
      </div>
    </section>
  );
}

// ── The demo request form ──
function DemoForm({ d, lang }) {
  const t0 = useRef(Date.now());
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    work_email: "",
    phone: "",
    trade: "",
    state: "",
    preferred_contact_method: "email",
    message: "",
    hp: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim())    e.full_name    = d.err_required;
    if (!form.company_name.trim()) e.company_name = d.err_required;
    if (!form.work_email.trim())   e.work_email   = d.err_required;
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.work_email.trim())) e.work_email = d.err_email;
    if (!form.phone.trim())        e.phone        = d.err_required;
    else if (form.phone.replace(/\D/g, "").length < 7) e.phone = d.err_phone;
    if (!form.trade.trim())        e.trade        = d.err_required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        language: lang,
        ttf_ms: Date.now() - t0.current,
      };
      const { data } = await axios.post(`${API}/public/demo-requests`, payload);
      if (data && data.ok) {
        setSubmitted(true);
        toast.success(d.toast_sent);
      } else {
        toast.error(d.toast_failed);
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      if (code === "invalid_work_email") setErrors({ work_email: d.err_email });
      else if (code === "invalid_phone") setErrors({ phone: d.err_phone });
      else if (err?.response?.status === 429) toast.error(d.toast_rate_limited);
      else toast.error(d.toast_failed);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="demo-form-success"
        className="relative bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(15,23,42,0.20)] border border-[var(--jc-border)] p-8 lg:p-10"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] mb-5">
          <CalendarCheck2 size={26} strokeWidth={2.2} />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-[var(--jc-text)]">{d.success_title}</h2>
        <p className="mt-3 text-[var(--jc-text-soft)] leading-relaxed">{d.success_body}</p>
        <p className="mt-6 text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-[var(--jc-teal)]" /> {d.form_privacy_note}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="demo-form"
      className="relative bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(15,23,42,0.20)] border border-[var(--jc-border)] p-6 sm:p-8"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--jc-navy)] text-white inline-flex items-center justify-center">
          <CalendarCheck2 size={20} strokeWidth={2.2} />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[var(--jc-text)]" data-testid="demo-form-title">
            {d.form_title}
          </h2>
          <p className="mt-1 text-sm text-[var(--jc-text-soft)] leading-relaxed">{d.form_subtitle}</p>
        </div>
      </div>

      {/* Honeypot — visually hidden, real users never fill it. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label>Website</label>
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.hp}
          onChange={(e) => set("hp", e.target.value)}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={d.f_full_name} required error={errors.full_name} testid="demo-field-full_name">
          <input className={inputBase} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder={d.ph_full_name} data-testid="demo-input-full_name" />
        </Field>
        <Field label={d.f_company_name} required error={errors.company_name} testid="demo-field-company_name">
          <input className={inputBase} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder={d.ph_company_name} data-testid="demo-input-company_name" />
        </Field>
        <Field label={d.f_work_email} required error={errors.work_email} testid="demo-field-work_email">
          <input type="email" className={inputBase} value={form.work_email} onChange={(e) => set("work_email", e.target.value)} placeholder={d.ph_work_email} data-testid="demo-input-work_email" />
        </Field>
        <Field label={d.f_phone} required error={errors.phone} testid="demo-field-phone">
          <input type="tel" className={inputBase} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={d.ph_phone} data-testid="demo-input-phone" />
        </Field>
        <Field label={d.f_trade} required error={errors.trade} testid="demo-field-trade">
          <select className={inputBase} value={form.trade} onChange={(e) => set("trade", e.target.value)} data-testid="demo-input-trade">
            <option value="">{d.ph_trade}</option>
            {TRADES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </Field>
        <Field label={d.f_state} testid="demo-field-state">
          <select className={inputBase} value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="demo-input-state">
            <option value="">{d.ph_state}</option>
            {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <div className={fieldLabel}>{d.f_preferred_contact}</div>
        <div className="flex flex-wrap gap-2" data-testid="demo-preferred-contact-group">
          {["phone", "text", "email"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set("preferred_contact_method", k)}
              data-testid={`demo-pref-${k}`}
              className={
                "inline-flex items-center px-3.5 py-2 rounded-md text-sm font-semibold border transition " +
                (form.preferred_contact_method === k
                  ? "bg-[var(--jc-navy)] text-white border-[var(--jc-navy)]"
                  : "bg-white text-[var(--jc-text)] border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]")
              }
            >
              {d.pref_labels[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <div className={fieldLabel}>{d.f_message} <span className="normal-case font-normal tracking-normal text-[var(--jc-text-soft)] text-[11px]">({d.optional})</span></div>
        <textarea
          className={inputBase}
          rows={3}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder={d.ph_message}
          data-testid="demo-input-message"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="demo-submit-btn"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md jc-btn-blue px-5 py-3.5 text-[15px] font-semibold text-white shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? d.submitting : d.submit} {!submitting && <ArrowRight size={16} />}
      </button>

      <p className="mt-4 text-xs text-[var(--jc-text-soft)] inline-flex items-center gap-1.5">
        <ShieldCheck size={13} className="text-[var(--jc-teal)]" /> {d.form_privacy_note}
      </p>
    </form>
  );
}

function Field({ label, required, error, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className={fieldLabel}>
        {label}{required && <span className="text-[var(--jc-blue)] ml-0.5">*</span>}
      </span>
      {children}
      {error && <div className="mt-1 text-xs font-semibold text-[#B45309]" data-testid={`${testid}-error`}>{error}</div>}
    </label>
  );
}

// ── "In your demo, we'll show you" tile row (dark band) ──
function DemoValueTiles({ d }) {
  const icons = [Users, ClipboardCheck, Sparkles, MessageCircleQuestion];
  return (
    <section className="bg-[var(--jc-navy)] text-white" data-testid="demo-value-band">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
        <h2 className="text-center font-display text-2xl sm:text-3xl">{d.value_heading}</h2>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {d.value_tiles.map((tile, i) => {
            const Icon = icons[i] || Sparkles;
            return (
              <div
                key={i}
                data-testid={`demo-value-tile-${i}`}
                className="rounded-xl bg-white/[0.04] border border-white/10 p-5 lg:p-6"
              >
                <div className="w-11 h-11 rounded-full bg-[var(--jc-blue)]/15 text-[var(--jc-blue)] inline-flex items-center justify-center mb-4">
                  <Icon size={20} strokeWidth={2.2} />
                </div>
                <div className="font-display text-lg leading-tight">{tile.title}</div>
                <p className="mt-2 text-sm text-white/75 leading-relaxed">{tile.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── FAQ using Radix accordion ──
function DemoFAQ({ d }) {
  return (
    <section className="bg-[var(--jc-bg)]" data-testid="demo-faq">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="text-center mb-10">
          <div className="text-[12px] font-bold tracking-[0.18em] uppercase text-[var(--jc-blue)]">{d.faq_eyebrow}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[var(--jc-text)]">{d.faq_heading}</h2>
        </div>
        <Accordion type="single" collapsible className="grid md:grid-cols-2 gap-x-8">
          {d.faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`demo-faq-item-${i}`}
              className="border-b border-[var(--jc-border)]"
            >
              <AccordionTrigger className="text-left font-display text-base sm:text-lg py-4 text-[var(--jc-text)] hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm text-[var(--jc-text-soft)] leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ── Final CTA row ──
function DemoFinalCTA({ d }) {
  const scrollTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { /* noop */ }
  };
  return (
    <section className="bg-[var(--jc-navy)] text-white" data-testid="demo-final-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-12 flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[var(--jc-blue)]/20 text-[var(--jc-blue)] inline-flex items-center justify-center">
            <CalendarCheck2 size={20} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg sm:text-xl leading-tight">{d.final_title}</div>
            <div className="text-sm text-white/70 mt-1">{d.final_body}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={scrollTop}
          data-testid="demo-final-cta-btn"
          className="inline-flex items-center gap-2 rounded-md jc-btn-blue px-5 py-3 font-semibold text-white text-sm shadow-lg whitespace-nowrap"
        >
          {d.final_cta} <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
}
